"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSlider = void 0;
const react_1 = __importDefault(require("react"));
const use_slide_gesture_1 = require("./use-slide-gesture");
const use_slider_springs_1 = require("./use-slider-springs");
const clamp_1 = require("./utils/clamp");
function useSlider({ sliderWidth, totalSlideCount, visibleSlideCount = 1, offset, disableDrag, onDrag, onOffsetChange }) {
    const enabled = sliderWidth > 0 && totalSlideCount > visibleSlideCount;
    const maxOffset = enabled ? totalSlideCount - visibleSlideCount : 0;
    const clampedOffset = (0, clamp_1.clamp)(offset, 0, maxOffset);
    const pageIndex = Math.ceil(clampedOffset / visibleSlideCount);
    const pageCount = Math.ceil(totalSlideCount / visibleSlideCount);
    const slideWidth = sliderWidth / visibleSlideCount;
    const dragThreshold = slideWidth / 3;
    const offsetRef = react_1.default.useRef(clampedOffset);
    // Reflect the offset in a ref to always use the current value when dragging.
    offsetRef.current = clampedOffset;
    const handleOffsetChange = react_1.default.useCallback((newOffset, direction, source) => {
        const newClampedOffset = (0, clamp_1.clamp)(newOffset, 0, maxOffset);
        if (clampedOffset !== newClampedOffset) {
            onOffsetChange(newClampedOffset, direction, source);
        }
    }, [clampedOffset, maxOffset, onOffsetChange]);
    const [springs, setSpringProps] = (0, use_slider_springs_1.useSliderSprings)({
        offsetRef,
        slideWidth,
        sliderWidth,
        totalSlideCount,
        visibleSlideCount,
        immediate: disableDrag
    });
    const bindGestureHandlers = (0, use_slide_gesture_1.useSlideGesture)({
        enabled,
        sliderWidth,
        dragThreshold,
        onDrag: relativeDelta => {
            if (disableDrag) {
                return;
            }
            setSpringProps(relativeDelta);
            if (onDrag) {
                onDrag(relativeDelta);
            }
        },
        onDragThresholdExceeded: direction => {
            const stopLeft = clampedOffset === 0 && direction === 'right';
            const stopRight = clampedOffset === maxOffset && direction === 'left';
            if (disableDrag || stopLeft || stopRight) {
                return false;
            }
            const offsetDelta = direction === 'left' ? 1 : -1;
            handleOffsetChange(clampedOffset + offsetDelta, offsetDelta === 1 ? 'next' : 'previous', 'drag');
            return true;
        }
    });
    const previous = react_1.default.useCallback(() => {
        handleOffsetChange(clampedOffset - visibleSlideCount, 'previous', 'external');
    }, [handleOffsetChange, clampedOffset, visibleSlideCount]);
    const next = react_1.default.useCallback(() => {
        handleOffsetChange(clampedOffset + visibleSlideCount, 'next', 'external');
    }, [handleOffsetChange, clampedOffset, visibleSlideCount]);
    react_1.default.useLayoutEffect(() => {
        setSpringProps();
    }, [sliderWidth, clampedOffset]);
    return {
        pageIndex,
        pageCount,
        springs,
        setSpringProps,
        bindGestureHandlers,
        previous,
        next
    };
}
exports.useSlider = useSlider;
